import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import AgGrid from 'components/molecules/agGrid';
import { contactsSortOptions } from 'components/organisms/contactsFilters/defaultFilters';
import { useContactsTableRows, useSortableColumns } from './hooks/useContactsTableRows';
import { ContactRenderer } from './contactsRenderers/contactRenderer';
import { ContactNameRenderer } from './contactsRenderers/contactNameRenderer';
import { ContactLinkRenderer } from './contactsRenderers/contactLinkRenderer';
import { ContactEmailRenderer } from './contactsRenderers/contactEmailRenderer';
import { ContactOfRenderer } from './contactsRenderers/contactOfRenderer';
import { ContactCompanyRenderer } from './contactsRenderers/contactCompanyRenderer';
import { ContactTagsRenderer } from './contactsRenderers/contactTagsRenderer';
import { ContactListRenderer } from './contactsRenderers/contactListRenderer';
import { ContactResumeRenderer } from './contactsRenderers/contactResumeRenderer';
import { useContactsSort } from './hooks/useContactsSort';
import { ContactNoteRenderer } from './contactsRenderers/contactNoteRenderer';
import { ContactSourceRender } from './contactsRenderers/contactSourceRender';
import { CustomFieldTextRenderer } from './contactsRenderers/customFieldTextRenderer';
import { CustomFieldSingleSelectRenderer } from './contactsRenderers/customFieldSingleSelectRenderer';
import { CustomFieldSingleSelectRowRenderer } from './contactsRenderers/customFieldSingleSelecRowRenderer';
import { useCellEditActions } from './hooks/useCellEditActions';
import { ContactJobApplicationRenderer } from './contactsRenderers/contactJobApplicationRenderer';

export const ContactsTableView = forwardRef(
  (
    {
      data,
      customFields,
      activeNetwork,
      listId,
      filtersCount,
      onSetFilters,
      onSaveFilters,
      savedFilters,
      isSavingFilters,
      filtersVisible,
      queryParams,
      page,
      deleteContactFromList,
      addedToSharedList,
      showSkeleton,
      list,
      columns,
      setColumns,
      selectedAll,
      onSelectAll,
      onChangeSelectedContact,
      selectedRows,
      onLoadMore,
      hasMore,
      isLoadingMore,
      meta,
      type,
      disabledContacts,
      sx,
    },
    ref,
  ) => {
    const rows = useContactsTableRows(data, {
      page,
      queryParams,
      deleteContactFromList,
      addedToSharedList,
      activeNetwork,
      listId,
      showLoader: showSkeleton,
      list,
      selectedRows,
      disabledContacts,
      type,
    });
    const { sortableColumns, initialSortBy } = useSortableColumns({
      type,
      page,
      listId,
      list,
      meta,
      activeNetwork,
      queryParams,
      customFields,
    });
    const onSort = useContactsSort({ activeNetwork, onSetFilters, queryParams, listId, page, list });
    const { onCellValueChanged } = useCellEditActions();

    if (meta?.queryTotal <= 0 && data.length === 0 && !showSkeleton) {
      return null;
    }

    return (
      <AgGrid
        ref={ref}
        disablePinnedOnMobile
        selectedAll={selectedAll}
        initialSortBy={initialSortBy}
        sortColumns={sortableColumns}
        enableLeftTableBorder={false}
        useExternalStoredColumns
        storedColumns={columns}
        setStoredColumns={setColumns}
        itemName="company"
        isInitialized
        canSort={false}
        onSort={([item]) => {
          const sortOption = contactsSortOptions.find((option) => option.field === item.id);

          if (!sortOption) {
            return;
          }

          onSort({
            value: sortOption.value,
            direction: item.desc ? 'desc' : 'asc',
            field: sortOption.field,
          });
        }}
        canSelect={type !== 'public' || (type === 'public' && list?.introRequestsEnabled)}
        hasMore={hasMore}
        loadMore={onLoadMore}
        items={rows}
        totalItems={rows.length}
        columnDefinition={columns}
        isLoadingMore={isLoadingMore}
        onColumnAction={() => {}}
        persistColumnKey={`${activeNetwork.id}-${listId}-contacts-columns-v1`}
        showActions={false}
        cellStyle={{
          padding: '0px',
        }}
        components={{
          contactRenderer: ContactRenderer,
          contactNameRenderer: ContactNameRenderer,
          contactLinkRenderer: ContactLinkRenderer,
          contactEmailRenderer: ContactEmailRenderer,
          contactOfRenderer: ContactOfRenderer,
          contactCompanyRenderer: ContactCompanyRenderer,
          contactTagsRenderer: ContactTagsRenderer,
          contactListRenderer: ContactListRenderer,
          contactResumeRenderer: ContactResumeRenderer,
          contactNoteRenderer: ContactNoteRenderer,
          contactSourceRenderer: ContactSourceRender,
          customFieldTextRenderer: CustomFieldTextRenderer,
          customFieldSingleSelectRenderer: CustomFieldSingleSelectRenderer,
          customFieldSingleSelectRowRenderer: CustomFieldSingleSelectRowRenderer,
          contactJobApplicationRenderer: ContactJobApplicationRenderer,
        }}
        filtersTitle="Filter contacts"
        filterCount={filtersCount}
        onSetFilters={onSetFilters}
        onSaveFilters={onSaveFilters}
        FiltersComponent={null}
        companyFilters={{
          savedFilters,
          isSavingFilters,
          showListFilters: filtersVisible,
          filtersTitle: 'Filter contacts',
          filters: queryParams.filters,
          filterCount: filtersCount,
          setFilters: onSetFilters,
          setFiltersVisible: () => {},
          saveFilters: onSaveFilters,
          filtersVisible,
        }}
        rowActions={{
          height: 330,
          items: [],
        }}
        sx={{
          height: 'calc(100vh - 100px)',
          '.ag-root-wrapper': {
            border: 'none',
          },
          '@media screen and (max-width: 992px)': {
            '[col-id="selected"], .ag-pinned-left-header ': {
              borderRight: 'none !important',
            },
          },
          ...sx,
        }}
        gridOptions={{
          getRowClass: () => {},
          onCellValueChanged,
        }}
        onSelect={(item, checked, isBulkSelection) => {
          if (isBulkSelection) {
            onSelectAll(checked);
          } else {
            onChangeSelectedContact(item?.index, item);
          }
        }}
      />
    );
  },
);

ContactsTableView.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  customFields: PropTypes.arrayOf(PropTypes.object),
  activeNetwork: PropTypes.object,
  listId: PropTypes.string,
  filtersCount: PropTypes.number,
  onSetFilters: PropTypes.func,
  onSaveFilters: PropTypes.func,
  savedFilters: PropTypes.array,
  isSavingFilters: PropTypes.bool,
  filtersVisible: PropTypes.bool,
  queryParams: PropTypes.object,
  page: PropTypes.string,
  deleteContactFromList: PropTypes.bool,
  addedToSharedList: PropTypes.bool,
  showSkeleton: PropTypes.bool,
  list: PropTypes.object,
  columns: PropTypes.array,
  setColumns: PropTypes.func,
  onChangeSelectedContact: PropTypes.func,
  selectedAll: PropTypes.bool,
  onSelectAll: PropTypes.func,
  selectedRows: PropTypes.arrayOf(PropTypes.number),
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  meta: PropTypes.object,
  type: PropTypes.string,
  disabledContacts: PropTypes.arrayOf(PropTypes.number),
  sx: PropTypes.object,
};

ContactsTableView.defaultProps = {
  customFields: [],
  activeNetwork: {},
  listId: '',
  filtersCount: 0,
  onSetFilters: () => {},
  onSaveFilters: () => {},
  savedFilters: [],
  isSavingFilters: false,
  filtersVisible: false,
  queryParams: {},
  page: '',
  deleteContactFromList: false,
  addedToSharedList: false,
  showSkeleton: false,
  list: {},
  columns: [],
  setColumns: () => {},
  onChangeSelectedContact: () => {},
  selectedAll: false,
  onSelectAll: () => {},
  selectedRows: [],
  onLoadMore: () => {},
  hasMore: false,
  isLoadingMore: false,
  meta: {},
  type: 'default',
  disabledContacts: [],
  sx: {},
};
